import { useReportStore } from '../../../stores/useReportStore';
import type { AuditVertical } from '../../../types/audit';
import { labelsFor } from '../../../data/reportLabels';
import SWOTGrid from '../widgets/SWOTGrid';
import DataQualityCard from '../widgets/DataQualityCard';
import Section from '../widgets/Section';
import StatusPill from '../widgets/StatusPill';
import GradeDot from '../widgets/GradeDot';
import { gradeTone, toneFor } from '../widgets/tone';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';

// Safely extract an array from data that might be an object with a nested array
function asArray<T>(val: unknown, ...keys: string[]): T[] {
  if (Array.isArray(val)) return val as T[];
  if (val && typeof val === 'object') {
    for (const key of keys) {
      const nested = (val as Record<string, unknown>)[key];
      if (Array.isArray(nested)) return nested as T[];
    }
  }
  return [];
}

// Normalize SWOT: might be { swot: { strengths: [...] } } or { strengths: [...] }
function normalizeSWOT(val: unknown): { strengths: unknown[]; weaknesses: unknown[]; opportunities: unknown[]; threats: unknown[] } {
  const empty = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
  if (!val || typeof val !== 'object') return empty;
  const obj = val as Record<string, unknown>;
  // If nested under .swot key
  if (obj.swot && typeof obj.swot === 'object') {
    const inner = obj.swot as Record<string, unknown>;
    return {
      strengths: asArray(inner.strengths),
      weaknesses: asArray(inner.weaknesses),
      opportunities: asArray(inner.opportunities),
      threats: asArray(inner.threats),
    };
  }
  return {
    strengths: asArray(obj.strengths),
    weaknesses: asArray(obj.weaknesses),
    opportunities: asArray(obj.opportunities),
    threats: asArray(obj.threats),
  };
}

// Normalize peers: might be { comparison: [...] } or array
function normalizePeers(val: unknown): Array<{ name: string; revenue: string; growth: string; subscribers: string; ltv: string; churn: string; cpa: string; position: string }> {
  const arr = asArray<Record<string, unknown>>(val, 'comparison');
  return arr.map((p) => ({
    name: String(p.company || p.name || ''),
    revenue: String(p.revenue || ''),
    growth: String(p.growth || ''),
    subscribers: String(p.subscribers || ''),
    ltv: String(p.ltv || ''),
    churn: String(p.churn || ''),
    cpa: String(p.cpa || ''),
    position: p.is_self ? 'you' : String(p.position || p.indicator || ''),
  }));
}

// Normalize macroRisks: might be { categories: { economic_climate: [...] } } or { economy: [...] }
function normalizeMacroRisks(val: unknown): Record<string, Array<{ text: string; impact: string }>> {
  if (!val || typeof val !== 'object') return {};
  const obj = val as Record<string, unknown>;

  // If it has a categories key, use that
  const cats = (obj.categories || obj) as Record<string, unknown>;

  const keyMap: Record<string, string> = {
    economic_climate: 'Economy',
    geopolitical_factors: 'Geopolitical',
    industry_dynamics: 'Industry',
    technology_shifts: 'Technology',
    economy: 'Economy',
    geopolitical: 'Geopolitical',
    industry: 'Industry',
    technology: 'Technology',
  };

  const result: Record<string, Array<{ text: string; impact: string }>> = {};
  for (const [key, label] of Object.entries(keyMap)) {
    const items = asArray<Record<string, unknown>>(cats, key);
    if (items.length > 0) {
      result[label] = items.map((item) => ({
        text: String(item.text || ''),
        impact: String(item.impact || '').toLowerCase().replace(/\s+/g, '-'),
      }));
    }
  }
  return result;
}

const AREA_BARS: Record<string, string> = {
  ok: 'bg-success',
  warn: 'bg-warning',
  crit: 'bg-destructive',
  neutral: 'bg-muted-foreground/60',
  info: 'bg-info',
};

const TH = 'bg-muted/40 font-mono text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap';
const NUM = 'font-mono text-[13px] tabular-nums whitespace-nowrap';

/** Rows named like "median"/"benchmark" get the prototype's italic-muted
 * treatment — they're a reference line, not a competitor. */
function isMedianRow(name: string): boolean {
  return /median|benchmark|industry avg/i.test(name);
}

export default function Analysis() {
  const data = useReportStore((s) => s.reportData);
  const L = labelsFor((data as Record<string, unknown> | null)?._vertical as AuditVertical | undefined);
  if (!data) return null;

  const businessAreas = asArray<Record<string, unknown>>(data.businessAreas, 'grades');
  const swot = normalizeSWOT(data.swot);
  const peers = normalizePeers(data.peers);
  const macroRisks = normalizeMacroRisks(data.macroRisks);

  return (
    <div>
      {/* Data Quality */}
      <Section
        title="Data quality assessment"
        hint="Completeness and accuracy of every connected platform feeding this report."
      >
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {asArray<Record<string, unknown>>(data.dataQuality).map((item, i) => (
            <DataQualityCard key={String(item.platform || i)} item={item as any} />
          ))}
          {asArray(data.dataQuality).length === 0 && (
            <p className="text-sm text-muted-foreground">No data quality issues detected.</p>
          )}
        </div>
      </Section>

      {/* SWOT */}
      <Section
        title="SWOT analysis"
        hint="Internal strengths and weaknesses against external opportunities and threats."
      >
        <SWOTGrid swot={swot as any} />
      </Section>

      {/* Business Area Assessment */}
      {businessAreas.length > 0 && (
        <Section title="Business area assessment" hint="Graded evaluation across core business dimensions.">
          <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {businessAreas.map((area) => {
              const grade = String(area.grade || '');
              const score = Number(area.score || 0);
              return (
                <Card size="sm" key={String(area.area)}>
                  <CardContent className="px-4.5 py-1">
                    <div className="flex items-center justify-between gap-2.5">
                      <span className="text-sm font-semibold text-foreground">{String(area.area)}</span>
                      <GradeDot grade={grade} />
                    </div>
                    <div className="mt-2.5 text-[28px] font-bold leading-none tracking-[-0.025em] tabular-nums text-foreground">
                      {score}
                      <span className="text-[13px] font-medium tracking-normal text-muted-foreground/70">/100</span>
                    </div>
                    <div className="mb-3 mt-2 h-[5px] overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn('h-full rounded-full', AREA_BARS[gradeTone(grade)])}
                        style={{ width: `${score}%` }}
                      />
                    </div>
                    <p className="text-xs leading-relaxed text-muted-foreground text-pretty">{String(area.detail || '')}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </Section>
      )}

      {/* Peer Comparison */}
      {peers.length > 0 && (
        <Section
          title="Peer comparison"
          hint="How key metrics compare against competitors and the industry median."
        >
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[640px]">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      {['Company', 'Revenue', 'Growth', L.activeCount, 'LTV', L.repeatMetric, 'CPA'].map((h) => (
                        <TableHead key={h} className={TH}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {peers.map((peer) => {
                      const you = peer.position === 'you';
                      const median = !you && isMedianRow(peer.name);
                      return (
                        <TableRow
                          key={peer.name}
                          className={cn(
                            you && 'bg-secondary/[.06] font-medium hover:bg-secondary/10',
                            median && 'italic text-muted-foreground',
                          )}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2 font-semibold text-foreground">
                              {!median && (
                                <img
                                  src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(peer.name.toLowerCase().replace(/\s+/g, ''))}.com&sz=32`}
                                  alt=""
                                  className="size-5 shrink-0 rounded-sm object-contain"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              )}
                              <span>
                                {peer.name}
                                {you && (
                                  <span className="block text-[11.5px] font-normal text-muted-foreground">This report</span>
                                )}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className={NUM}>{peer.revenue}</TableCell>
                          <TableCell className={NUM}>{peer.growth}</TableCell>
                          <TableCell className={NUM}>{peer.subscribers}</TableCell>
                          <TableCell className={NUM}>{peer.ltv}</TableCell>
                          <TableCell className={NUM}>{peer.churn}</TableCell>
                          <TableCell className={NUM}>{peer.cpa}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </Section>
      )}

      {/* Macro Risks */}
      {Object.keys(macroRisks).length > 0 && (
        <Section
          title="Macro risk assessment"
          hint="External factors and market dynamics that could impact the business trajectory."
        >
          <div className="grid items-start gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {Object.entries(macroRisks).map(([category, items]) => (
              <Card size="sm" key={category}>
                <CardContent className="px-5 py-1">
                  <h3 className="text-sm font-bold tracking-[-0.01em] text-foreground">{category}</h3>
                  {items.map((item, i) => {
                    const impact = item.impact.replace(/-/g, ' ');
                    return (
                      <div key={i} className="pt-3.5">
                        <StatusPill tone={toneFor(impact)}>{impact}</StatusPill>
                        <p className="mt-2 text-[13px] leading-relaxed text-foreground/80 text-pretty">{item.text}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
